import { supabase } from '../lib/supabase';

class PatientService {
  /**
   * Get all patients for a psychologist with appointment statistics
   */
  async getPatients(psychologistId, filters = {}) {
    try {
      // First get patients
      let patientsQuery = supabase
        ?.from('patients')
        ?.select('*')
        ?.eq('psychologist_id', psychologistId)
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        patientsQuery = patientsQuery?.eq('status', filters?.status);
      }

      if (filters?.search) {
        patientsQuery = patientsQuery?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%,phone.ilike.%${filters?.search}%`);
      }

      const { data: patients, error: patientsError } = await patientsQuery;

      if (patientsError) throw patientsError;

      // Get appointment statistics for all patients
      const patientIds = patients?.map(p => p?.id) || [];
      
      if (patientIds?.length > 0) {
        // Get completed appointments count and last visit for each patient
        const { data: appointmentStats, error: statsError } = await supabase
          ?.from('appointments')
          ?.select('patient_id, appointment_date, status')
          ?.in('patient_id', patientIds)
          ?.in('status', ['completed', 'confirmed', 'in_progress'])
          ?.order('appointment_date', { ascending: false });

        if (statsError) throw statsError;

        // Calculate stats for each patient
        const patientStatsMap = {};
        appointmentStats?.forEach(appointment => {
          const patientId = appointment?.patient_id;
          
          if (!patientStatsMap?.[patientId]) {
            patientStatsMap[patientId] = {
              totalSessions: 0,
              lastVisit: null
            };
          }
          
          // Count completed sessions
          if (appointment?.status === 'completed') {
            patientStatsMap[patientId].totalSessions += 1;
          }
          
          // Track most recent appointment date
          if (!patientStatsMap?.[patientId]?.lastVisit || 
              new Date(appointment?.appointment_date) > new Date(patientStatsMap[patientId].lastVisit)) {
            patientStatsMap[patientId].lastVisit = appointment?.appointment_date;
          }
        });

        // Merge stats with patient data
        const patientsWithStats = patients?.map(patient => ({
          ...patient,
          totalSessions: patientStatsMap?.[patient?.id]?.totalSessions || 0,
          lastVisit: patientStatsMap?.[patient?.id]?.lastVisit || null
        }));

        return { data: patientsWithStats, error: null };
      }

      // If no patients, return empty array
      return { data: patients || [], error: null };
    } catch (error) {
      console.error('Get patients error:', error);
      return { data: null, error };
    }
  }

  /**
   * Get patient by ID
   */
  async getPatientById(id) {
    try {
      const { data, error } = await supabase?.from('patients')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get patient error:', error);
      return { data: null, error };
    }
  }

  /**
   * Create new patient
   */
  async createPatient(patientData) {
    try {
      const { data, error } = await supabase?.from('patients')?.insert([patientData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create patient error:', error);
      return { data: null, error };
    }
  }

  /**
   * Update patient
   */
  async updatePatient(id, updates) {
    try {
      const { data, error } = await supabase?.from('patients')?.update(updates)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update patient error:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete patient
   */
  async deletePatient(id) {
    try {
      const { error } = await supabase?.from('patients')?.delete()?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete patient error:', error);
      return { error };
    }
  }

  /**
   * Delete temp_public_patients
   */
  async deleteTempPatient(id) {
    try {
      const { error } = await supabase?.from('temp_public_patients')?.delete()?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete patient error:', error);
      return { error };
    }
  }

  /**
   * Get patient statistics
   */
  async getPatientStats(psychologistId) {
    try {
      const { data, error } = await supabase?.from('patients')?.select('status')?.eq('psychologist_id', psychologistId);

      if (error) throw error;

      const stats = {
        total: data?.length,
        active: data?.filter(p => p?.status === 'active')?.length,
        inactive: data?.filter(p => p?.status === 'inactive')?.length,
        discharged: data?.filter(p => p?.status === 'discharged')?.length
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Get patient stats error:', error);
      return { data: null, error };
    }
  }
}

export const patientService = new PatientService();